// src/features/screener/components/ScreenerResultsTable.tsx
//
// The results grid for the Global Screener — a dense, Bloomberg-terminal
// style table. Sorting is SERVER-SIDE: clicking a numeric column header
// asks the backend to re-order the FULL filtered universe by that column
// (asc/desc) and return the true top-N, rather than re-sorting only the
// loaded page. Clicking a row routes to the matching asset deep-dive page.

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { AssetClass, ScreenerResult, ScreenerSortKey } from '../api';
import {
    formatMoney,
    formatCompactMoney,
    type CurrencyContext,
} from '../../../shared/utils/currency';

interface ScreenerResultsTableProps {
    results: ScreenerResult[];
    onRowClick: (result: ScreenerResult) => void;
    /** The active server-side sort column. */
    sortBy: ScreenerSortKey;
    /** True = descending, false = ascending. */
    sortDesc: boolean;
    /** Fired when a sortable header is clicked (page issues a refetch). */
    onSortChange: (key: ScreenerSortKey) => void;
    /** Dims the grid while a refetch is in flight (live-update feel). */
    isRefreshing?: boolean;
    /** When true, drops the irrelevant Sector/Beta/Dividend columns. */
    isCryptoMode?: boolean;
}

const BADGE_STYLE_BY_CLASS: Record<AssetClass, string> = {
    equity:
        'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    etf: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    reit: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    crypto:
        'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400 border-orange-200 dark:border-orange-800',
};

const getBadgeStyle = (assetClass: AssetClass): string =>
    BADGE_STYLE_BY_CLASS[assetClass] ??
    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700';

const compact = (val?: number | null): string =>
    val != null && Number.isFinite(val)
        ? new Intl.NumberFormat('en-US', {
              notation: 'compact',
              maximumFractionDigits: 2,
          }).format(val)
        : '—';

// Currency-aware money for a screener row: prefers the backend-provided
// `currency`, then falls back to the symbol/exchange mapping so EU rows
// (XETRA/Vienna) render `€` instead of `$`.
//
// MICRO-PRICE FIX: sub-dollar cryptos (e.g. Shiba Inu at ~$0.000014) round
// to a broken-looking "$0.00" under the standard 2-decimal money format, so
// crypto rows under $1 render up to 6 decimal places instead.
const money = (
    val: number | null | undefined,
    ctx: CurrencyContext,
    assetClass?: string,
): string => {
    if (val == null || !Number.isFinite(val)) return '—';

    if (assetClass === 'crypto' && val < 1.0 && val > 0) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: ctx.explicitCurrency || 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
        }).format(val);
    }

    return formatMoney(val, ctx);
};

// Market cap is shown USD-NORMALISED (not native currency) so the column is a
// like-for-like global comparison — a KRW cap and a USD cap sit on the same
// scale and the "largest companies" sort is meaningful. The backend already
// converts + sorts on this value; we simply force the `$` glyph here.
const usdCompact = (val: number | null | undefined): string =>
    formatCompactMoney(val, { explicitCurrency: 'USD' });

const decimal = (val?: number | null, digits = 2): string =>
    val != null && Number.isFinite(val) ? val.toFixed(digits) : '—';

// Column definitions. Only numeric columns carry a `sortKey`; those are
// the ones the backend can sort the full universe by (a click delegates
// to the server). The text columns (symbol / name / sector) are not
// server-sortable, so they render as plain, non-interactive headers.
const COLUMNS: {
    label: string;
    align: 'left' | 'right';
    sortKey?: ScreenerSortKey;
    hideForCrypto?: boolean;
}[] = [
    { label: 'Symbol', align: 'left' },
    { label: 'Name', align: 'left' },
    { label: 'Sector', align: 'left', hideForCrypto: true },
    { label: 'Price', align: 'right', sortKey: 'price' },
    { label: 'Market Cap (USD)', align: 'right', sortKey: 'market_cap' },
    { label: 'Beta', align: 'right', sortKey: 'beta', hideForCrypto: true },
    { label: 'Dividend', align: 'right', sortKey: 'dividend', hideForCrypto: true },
    { label: 'Volume', align: 'right', sortKey: 'volume' },
];

const PAGE_SIZE = 15;

export const ScreenerResultsTable = ({
    results,
    onRowClick,
    sortBy,
    sortDesc,
    onSortChange,
    isRefreshing = false,
    isCryptoMode = false,
}: ScreenerResultsTableProps) => {
    const [page, setPage] = useState(0);

    // Crypto has no Sector/Beta/Dividend, so skip building those DOM nodes
    // entirely. hideForCrypto is the single source of truth shared by the
    // <thead> and the <tbody> below, so headers and cells can never misalign.
    const visibleColumns = COLUMNS.filter(
        (col) => !(isCryptoMode && col.hideForCrypto),
    );

    const pageCount = Math.max(1, Math.ceil(results.length / PAGE_SIZE));

    // Snap back to the first page whenever the underlying result set changes
    // (a new filter / sort returns a fresh array), and never leave the user
    // stranded on a page that no longer exists.
    useEffect(() => {
        setPage(0);
    }, [results]);

    const safePage = Math.min(page, pageCount - 1);
    const pageStart = safePage * PAGE_SIZE;
    const pageEnd = Math.min(pageStart + PAGE_SIZE, results.length);
    const pageRows = useMemo(
        () => results.slice(pageStart, pageEnd),
        [results, pageStart, pageEnd],
    );

    // Compact, windowed page-number list (max 5 buttons centred on current).
    const pageWindow = useMemo(() => {
        const maxButtons = 5;
        let start = Math.max(0, safePage - Math.floor(maxButtons / 2));
        const end = Math.min(pageCount, start + maxButtons);
        start = Math.max(0, end - maxButtons);
        return Array.from({ length: end - start }, (_, i) => start + i);
    }, [safePage, pageCount]);

    return (
        <div
            className={`surface-card p-0 overflow-hidden transition-opacity ${
                isRefreshing ? 'opacity-50' : 'opacity-100'
            }`}
            aria-busy={isRefreshing}
        >
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40">
                            {visibleColumns.map((col) => {
                                const sortable = col.sortKey != null;
                                const isActive =
                                    sortable && col.sortKey === sortBy;
                                return (
                                    <th
                                        key={col.label}
                                        scope="col"
                                        onClick={
                                            sortable
                                                ? () => onSortChange(col.sortKey!)
                                                : undefined
                                        }
                                        aria-sort={
                                            isActive
                                                ? sortDesc
                                                    ? 'descending'
                                                    : 'ascending'
                                                : undefined
                                        }
                                        className={`px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 select-none whitespace-nowrap transition-colors ${
                                            sortable
                                                ? 'cursor-pointer hover:text-slate-900 dark:hover:text-white'
                                                : ''
                                        } ${
                                            col.align === 'right'
                                                ? 'text-right'
                                                : 'text-left'
                                        } ${isActive ? 'text-slate-900 dark:text-white' : ''}`}
                                    >
                                        <span
                                            className={`inline-flex items-center gap-1 ${
                                                col.align === 'right'
                                                    ? 'flex-row-reverse'
                                                    : ''
                                            }`}
                                        >
                                            {col.label}
                                            {sortable && (
                                                <span
                                                    className={`text-[9px] ${isActive ? 'opacity-100' : 'opacity-0'}`}
                                                >
                                                    {sortDesc ? '▼' : '▲'}
                                                </span>
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {pageRows.map((row) => {
                            const ctx: CurrencyContext = {
                                explicitCurrency: row.currency,
                                symbol: row.symbol,
                                exchange: row.exchange,
                            };
                            return (
                            <tr
                                key={row.symbol}
                                onClick={() => onRowClick(row)}
                                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                            >
                                {/* Symbol + class badge */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-900 dark:text-white tracking-tight">
                                            {row.symbol}
                                        </span>
                                        <span
                                            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${getBadgeStyle(row.asset_class)}`}
                                        >
                                            {row.asset_class}
                                        </span>
                                    </div>
                                    {row.exchange && (
                                        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">
                                            {row.exchange}
                                        </span>
                                    )}
                                </td>
                                {/* Name */}
                                <td className="px-4 py-3 text-slate-600 dark:text-slate-300 max-w-[240px] truncate">
                                    {row.name}
                                </td>
                                {/* Sector (hidden for crypto) */}
                                {!isCryptoMode && (
                                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                        {row.sector ?? '—'}
                                    </td>
                                )}
                                {/* Numeric ledger columns */}
                                <td className="px-4 py-3 text-right tabular-nums text-slate-900 dark:text-slate-100 whitespace-nowrap">
                                    {money(row.price, ctx, row.asset_class)}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-900 dark:text-slate-100 whitespace-nowrap">
                                    {usdCompact(row.market_cap_usd)}
                                </td>
                                {/* Beta (hidden for crypto) */}
                                {!isCryptoMode && (
                                    <td className="px-4 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                        {decimal(row.beta)}
                                    </td>
                                )}
                                {/* Dividend (hidden for crypto) */}
                                {!isCryptoMode && (
                                    <td className="px-4 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                        {money(row.dividend, ctx, row.asset_class)}
                                    </td>
                                )}
                                <td className="px-4 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                    {compact(row.volume)}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination footer — paginates the loaded (server-sorted) top-N
                result set client-side so the grid reads like a professional
                data terminal instead of one endless scroll. */}
            {results.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40 px-4 py-3">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 tabular-nums">
                        Showing{' '}
                        <span className="font-bold text-slate-700 dark:text-slate-200">
                            {pageStart + 1}–{pageEnd}
                        </span>{' '}
                        of{' '}
                        <span className="font-bold text-slate-700 dark:text-slate-200">
                            {results.length.toLocaleString()}
                        </span>
                    </p>

                    <div className="flex items-center gap-1">
                        <PagerButton
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            label="Previous page"
                        >
                            ‹
                        </PagerButton>

                        {pageWindow[0] > 0 && (
                            <>
                                <PageNumber
                                    n={0}
                                    active={safePage === 0}
                                    onClick={() => setPage(0)}
                                />
                                <span className="px-1 text-slate-400 select-none">…</span>
                            </>
                        )}

                        {pageWindow.map((n) => (
                            <PageNumber
                                key={n}
                                n={n}
                                active={n === safePage}
                                onClick={() => setPage(n)}
                            />
                        ))}

                        {pageWindow[pageWindow.length - 1] < pageCount - 1 && (
                            <>
                                <span className="px-1 text-slate-400 select-none">…</span>
                                <PageNumber
                                    n={pageCount - 1}
                                    active={safePage === pageCount - 1}
                                    onClick={() => setPage(pageCount - 1)}
                                />
                            </>
                        )}

                        <PagerButton
                            onClick={() =>
                                setPage((p) => Math.min(pageCount - 1, p + 1))
                            }
                            disabled={safePage >= pageCount - 1}
                            label="Next page"
                        >
                            ›
                        </PagerButton>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Pagination primitives (kept local to the feature). -----------------

const PagerButton = ({
    onClick,
    disabled,
    label,
    children,
}: {
    onClick: () => void;
    disabled: boolean;
    label: string;
    children: ReactNode;
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 dark:disabled:hover:border-slate-700 disabled:hover:text-slate-600 dark:disabled:hover:text-slate-300 transition-colors"
    >
        {children}
    </button>
);

const PageNumber = ({
    n,
    active,
    onClick,
}: {
    n: number;
    active: boolean;
    onClick: () => void;
}) => (
    <button
        type="button"
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg border px-2 text-sm font-semibold tabular-nums transition-colors ${
            active
                ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
                : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-400'
        }`}
    >
        {n + 1}
    </button>
);

export default ScreenerResultsTable;
